import React from 'react'
import PropTypes from 'prop-types'
import { IconCaretSelect } from 'secondstep-components'

import { ITEM_KINDS } from './constants'
import DropdownColumn from './DropdownColumn'
import { groupItemsIntoColumns } from './helpers'
import {
  DropdownPanel,
  DropdownPositioner,
  DropdownWrapper,
  TabCaret,
  TabSlot,
} from './component.styles'

const ProgramNavDropdown = ({
  children,
  isActive,
  isOpen,
  items,
  label,
  onBlur,
  onFocus,
  onItemClick,
  onKeyDown,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
  panelId,
  positionerRef,
  tabSlotRef,
  wrapperRef,
}) => (
  <DropdownWrapper
    onBlur={onBlur}
    onFocus={onFocus}
    onKeyDown={onKeyDown}
    onMouseDown={onMouseDown}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    ref={wrapperRef}
  >
    <TabSlot ref={tabSlotRef}>
      {/* Disclosure pattern: aria-haspopup is omitted because it implies an
          ARIA menu with arrow-key navigation, which this panel does not have. */}
      {React.isValidElement(children)
        ? React.cloneElement(children, {
            'aria-controls': isOpen ? panelId : undefined,
            'aria-expanded': isOpen,
          })
        : children}
      {/* Outside the link so clicking it never navigates; hovering it still
          opens the menu because it is inside the wrapper. */}
      <TabCaret
        aria-hidden="true"
        data-testid="program-nav-dropdown-caret"
        isActive={isActive}
      >
        <IconCaretSelect focusable="false" />
      </TabCaret>
    </TabSlot>
    {isOpen && (
      <DropdownPositioner ref={positionerRef}>
        <DropdownPanel
          aria-label={`${label} menu`}
          data-testid="program-nav-dropdown"
          id={panelId}
          role="group"
        >
          {groupItemsIntoColumns(items).map((column, index) => (
            <DropdownColumn
              column={column}
              columnId={`${panelId}-column-${index}`}
              key={`${column.kind}-${column.title}-${index}`}
              onItemClick={onItemClick}
            />
          ))}
        </DropdownPanel>
      </DropdownPositioner>
    )}
  </DropdownWrapper>
)

ProgramNavDropdown.propTypes = {
  children: PropTypes.node,
  isActive: PropTypes.bool,
  isOpen: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.oneOf(Object.values(ITEM_KINDS)).isRequired,
      title: PropTypes.string.isRequired,
      route: PropTypes.string,
      isSelected: PropTypes.bool,
      tier: PropTypes.number,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          route: PropTypes.string,
          isSelected: PropTypes.bool,
        }),
      ),
    }),
  ).isRequired,
  label: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onItemClick: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  panelId: PropTypes.string,
  positionerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  tabSlotRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  wrapperRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
}

export default ProgramNavDropdown
